import { useState } from "react";
import { Link } from "react-router-dom";
import { Heart, ShoppingBag } from "lucide-react";
import { useCart } from "@/context/CartContext";
import { useWishlist } from "@/context/WishlistContext";
import { useCurrency } from "@/context/CurrencyContext";

export const ProductCard = ({ product, index = 0 }) => {
  const { addItem } = useCart();
  const { has, toggle } = useWishlist();
  const { format: money } = useCurrency();
  const [activeColor, setActiveColor] = useState(
    product.colors && product.colors[0] ? product.colors[0].name : null
  );
  const wished = has(product.slug);
  const secondary = product.images[1] || product.images[0];
  const soldOut = product.in_stock === false || product.stock === 0;

  return (
    <div className="group flex flex-col gap-4" data-testid={`product-card-${product.slug}`}>
      <div className="relative overflow-hidden bg-beige aspect-[3/4] w-full rounded-sm">
        <Link to={`/product/${product.slug}`} aria-label={product.name}>
          <img
            src={product.images[0]}
            alt={product.name}
            loading="lazy"
            className="absolute inset-0 object-cover w-full h-full transition-opacity duration-700 group-hover:opacity-0"
          />
          <img
            src={secondary}
            alt={`${product.name} alternate`}
            loading="lazy"
            className="absolute inset-0 object-cover w-full h-full opacity-0 scale-105 transition-all duration-700 group-hover:opacity-100 group-hover:scale-100"
          />
        </Link>

        {/* Badges */}
        <div className="absolute top-3 left-3 flex flex-col gap-2">
          {soldOut && (
            <span className="bg-[#560216] text-white text-[10px] uppercase tracking-[0.2em] px-3 py-1 rounded-full" data-testid={`sold-out-badge-${product.slug}`}>
              Sold Out
            </span>
          )}
          {product.is_new && (
            <span className="bg-white/90 text-[#1A1A1A] text-[10px] uppercase tracking-[0.2em] px-3 py-1 rounded-full">
              New
            </span>
          )}
          {product.compare_at_price && (
            <span className="bg-sand text-white text-[10px] uppercase tracking-[0.2em] px-3 py-1 rounded-full">
              Sale
            </span>
          )}
        </div>

        {/* Wishlist */}
        <button
          onClick={() => toggle(product)}
          aria-label="Add to wishlist"
          data-testid={`wishlist-toggle-${product.slug}`}
          className="absolute top-3 right-3 h-9 w-9 grid place-items-center rounded-full bg-white/85 backdrop-blur transition-transform duration-300 hover:scale-110"
        >
          <Heart strokeWidth={1} className={`h-4 w-4 ${wished ? "fill-[#1A1A1A] text-[#1A1A1A]" : "text-[#1A1A1A]"}`} />
        </button>

        {/* Quick add */}
        {soldOut ? (
          <div
            data-testid={`sold-out-${product.slug}`}
            className="absolute bottom-3 left-3 right-3 flex items-center justify-center gap-2 bg-white/90 text-[#9a3b3b] text-[11px] uppercase tracking-[0.2em] py-3 rounded-full opacity-0 translate-y-3 transition-all duration-500 group-hover:opacity-100 group-hover:translate-y-0"
          >
            Sold Out
          </div>
        ) : (
          <button
            onClick={() => addItem(product, activeColor)}
            data-testid={`quick-add-${product.slug}`}
            className="absolute bottom-3 left-3 right-3 flex items-center justify-center gap-2 bg-[#1A1A1A] text-white text-[11px] uppercase tracking-[0.2em] py-3 rounded-full opacity-0 translate-y-3 transition-all duration-500 group-hover:opacity-100 group-hover:translate-y-0"
          >
            <ShoppingBag strokeWidth={1.2} className="h-4 w-4" /> Add to Bag
          </button>
        )}
      </div>

      <div className="flex flex-col gap-2">
        <span className="eyebrow">{product.category_label}</span>
        <Link to={`/product/${product.slug}`} className="font-serif text-xl leading-tight hover:opacity-70 transition-opacity">
          {product.name}
        </Link>
        <div className="flex items-center gap-3">
          <span className="text-sm">{money(product.price)}</span>
          {product.compare_at_price && (
            <span className="text-sm text-[#737373] line-through">{money(product.compare_at_price)}</span>
          )}
        </div>
        {product.colors && product.colors.length > 0 && (
          <div className="flex items-center gap-2 pt-1">
            {product.colors.map((c) => (
              <button
                key={c.name}
                onClick={() => setActiveColor(c.name)}
                aria-label={c.name}
                title={c.name}
                className={`h-4 w-4 rounded-full border transition-transform duration-200 hover:scale-110 ${
                  activeColor === c.name ? "ring-1 ring-offset-2 ring-[#1A1A1A] border-transparent" : "border-[#E5E5E5]"
                }`}
                style={{ backgroundColor: c.hex }}
              />
            ))}
          </div>
        )}
      </div>
    </div>
  );
};

export default ProductCard;
